"use client";

import {
  ChartLineUp,
  House,
  ImageSquare,
  ListChecks,
  Receipt,
  SealCheck,
  Target,
  Trophy,
  UsersThree,
} from "@phosphor-icons/react";
import { ReactNode } from "react";
import Image from "next/image";
import styles from "./kaizen-app.module.css";

export type View =
  | "today"
  | "members"
  | "projects"
  | "milestones"
  | "check-ins"
  | "evidence"
  | "reviews"
  | "analytics"
  | "fines";

export type Modal = "none" | "group" | "declare" | "evidence" | "review";

export const groupTimezones = [
  ["Africa/Nairobi", "Nairobi (EAT)"],
  ["Africa/Cairo", "Cairo (EET)"],
  ["Africa/Johannesburg", "Johannesburg (SAST)"],
  ["Africa/Lagos", "Lagos (WAT)"],
  ["UTC", "UTC"],
  ["Europe/London", "London (GMT/BST)"],
  ["Europe/Paris", "Paris (CET/CEST)"],
  ["Europe/Berlin", "Berlin (CET/CEST)"],
  ["Europe/Istanbul", "Istanbul (TRT)"],
  ["Asia/Dubai", "Dubai (GST)"],
  ["Asia/Kolkata", "Kolkata (IST)"],
  ["Asia/Bangkok", "Bangkok (ICT)"],
  ["Asia/Singapore", "Singapore (SGT)"],
  ["Asia/Shanghai", "Shanghai (CST)"],
  ["Asia/Tokyo", "Tokyo (JST)"],
  ["America/New_York", "New York (ET)"],
  ["America/Chicago", "Chicago (CT)"],
  ["America/Denver", "Denver (MT)"],
  ["America/Los_Angeles", "Los Angeles (PT)"],
  ["America/Sao_Paulo", "São Paulo (BRT)"],
  ["Australia/Sydney", "Sydney (AET)"],
  ["Pacific/Auckland", "Auckland (NZT)"],
] as const;

export function GroupTimezoneOptions() {
  return groupTimezones.map(([value, label]) => (
    <option key={value} value={value}>
      {label} · {value}
    </option>
  ));
}

export const navigation: Array<{
  id: View;
  label: string;
  icon: typeof House;
  count?: number;
}> = [
  { id: "today", label: "Today", icon: House },
  { id: "members", label: "Members", icon: UsersThree },
  { id: "projects", label: "Projects", icon: Target },
  { id: "milestones", label: "Milestones", icon: Trophy },
  { id: "check-ins", label: "Check-ins", icon: ListChecks },
  { id: "evidence", label: "Evidence", icon: ImageSquare },
  { id: "reviews", label: "Reviews", icon: SealCheck, count: 3 },
  { id: "analytics", label: "Analytics", icon: ChartLineUp },
  { id: "fines", label: "Fine ledger", icon: Receipt },
];

export function Brand({
  compact = false,
  onClick,
}: {
  compact?: boolean;
  onClick?: () => void;
}) {
  const className = `${styles.brand} ${compact ? styles.brandCompact : ""}`;
  const content = (
    <>
      <Image
        className={styles.brandLogo}
        src="/brand-mark.png"
        alt=""
        width={36}
        height={36}
        aria-hidden="true"
      />
      {!compact && <span>KAIZEN</span>}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={`${className} ${styles.brandButton}`}
        aria-label="Kaizen — Go to Today"
        onClick={onClick}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={className}>
      {content}
      {compact && <span className={styles.srOnly}>Kaizen</span>}
    </div>
  );
}

export function IconButton({
  label,
  children,
  onClick,
  badge,
}: {
  label: string;
  children: ReactNode;
  onClick?: () => void;
  badge?: boolean;
}) {
  return (
    <button className={styles.iconButton} aria-label={label} onClick={onClick}>
      {children}
      {badge && <span className={styles.notificationDot} />}
    </button>
  );
}
